import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { api, type BoutiqueConfig, type ShopCustomOrder, type ShopCustomOrderStatus } from "@/api";
import { ManufacturingWizard } from "@/components/product/manufacturing-wizard";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useProfile } from "@/store/profile";

// fd-boutique-13: вкладка «Индивидуальный заказ» витрины магазина — прайс
// продавца, запуск мастера и статусы заявок покупателя в этот магазин.

const rub = (n: number) => `${new Intl.NumberFormat("ru-RU").format(n)} руб.`;
const ruDate = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
};
export const customOrdersKey = (shopId?: string) => ["buyer", "custom-orders", shopId ?? "all"] as const;
const STATUS_TONE: Record<ShopCustomOrderStatus, string> = {
  sent: "bg-ds-gray text-ds-primary",
  in_progress: "bg-[#6982BA]/10 text-[#3E5C9A]",
  offer: "bg-ds-accent/10 text-ds-accent",
  accepted: "bg-ds-success/10 text-ds-success",
  cancelled: "bg-ds-gray text-ds-muted"
};
export function CustomOrderTab(t0) {
  const $ = _c(48);
  const {
    shop,
    config
  } = t0;
  const {
    t
  } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const settings = config.customOrder;
  let t1;
  if ($[0] !== settings?.productTypes) {
    t1 = settings?.productTypes ?? [];
    $[0] = settings?.productTypes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const types = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("shop.customOrder.title");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <h2 className="text-[20px] leading-6 font-bold tracking-[-0.6px] text-ds-ink">{t2}</h2>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== settings?.note || $[7] !== t) {
    t4 = settings?.note?.trim() || t("shop.customTab.body");
    $[6] = settings?.note;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <p className="max-w-2xl text-[14px] leading-5 font-medium text-ds-primary">{t4}</p>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <div className="flex flex-col gap-2">{t3}{t5}</div>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t || $[15] !== types) {
    t7 = types.length > 0 && <ul className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3" aria-label={t("shop.customOrder.priceList")}>{types.map(type => <li key={type.id} className="flex items-center justify-between gap-3 rounded-[12px] bg-white px-4 py-3"><span className="text-[14px] leading-[18px] font-semibold text-ds-primary">{type.name}</span><span className="shrink-0 text-[14px] leading-[18px] text-ds-secondary">{type.price ? t("shop.customOrder.priceFrom", {
            price: rub(type.price)
          }) : t("shop.customOrder.priceOnRequest")}</span></li>)}</ul>;
    $[14] = t;
    $[15] = types;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setOpen(true);
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t("shop.customOrder.start");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <Button size="md" onClick={t8} data-testid="custom-order-start">{t9}</Button>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== settings || $[23] !== t) {
    t11 = settings?.leadDays ? <span className="text-[12px] leading-4 text-ds-secondary">{t("shop.customOrder.leadDays", {
        count: settings.leadDays
      })}</span> : null;
    $[22] = settings;
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <div className="flex flex-wrap items-center gap-3">{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t6 || $[30] !== t7) {
    t13 = <section className="flex flex-col gap-5 rounded-[20px] bg-ds-quaternary p-5 md:p-8">{t6}{t7}{t12}</section>;
    $[28] = t12;
    $[29] = t6;
    $[30] = t7;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = shop.id;
  let t15;
  if ($[32] !== t) {
    t15 = t("shop.customOrder.myRequests");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== shop.id || $[35] !== t15) {
    t16 = <CustomOrderList shopId={t14} title={t15} />;
    $[34] = shop.id;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== qc) {
    t17 = () => void qc.invalidateQueries({
      queryKey: ["buyer", "custom-orders"]
    });
    $[37] = qc;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== open || $[40] !== settings || $[41] !== shop || $[42] !== t17) {
    t18 = <ManufacturingWizard open={open} onOpenChange={setOpen} shop={shop} settings={settings} onSubmitted={t17} />;
    $[39] = open;
    $[40] = settings;
    $[41] = shop;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t13 || $[45] !== t16 || $[46] !== t18) {
    t19 = <div className="flex flex-col gap-8" data-testid="custom-order-tab">{t13}{t16}{t18}</div>;
    $[44] = t13;
    $[45] = t16;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  return t19;
}

/** Заявки покупателя: одного магазина (вкладка витрины) или все («Запросы»). */
export function CustomOrderList(t0) {
  const $ = _c(31);
  const {
    shopId,
    title
  } = t0;
  const {
    t
  } = useTranslation();
  const qc = useQueryClient();
  const token = useProfile(_temp);
  let t1;
  if ($[0] !== shopId) {
    t1 = customOrdersKey(shopId);
    $[0] = shopId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== shopId) {
    t2 = () => api.buyer.listCustomOrders(shopId);
    $[2] = shopId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = Boolean(token);
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const list = useQuery(t4);
  let t5;
  if ($[8] !== qc) {
    t5 = () => void qc.invalidateQueries({
      queryKey: ["buyer", "custom-orders"]
    });
    $[8] = qc;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = e => toast.error(e instanceof Error ? e.message : t("common.error"));
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = {
      mutationFn: _temp2,
      onSuccess: t5,
      onError: t6
    };
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const cancel = useMutation(t7);
  let t8;
  if ($[15] !== list.data?.items) {
    t8 = list.data?.items ?? [];
    $[15] = list.data?.items;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const items = t8;
  if (!token || items.length === 0) {
    return null;
  }
  let t9;
  if ($[17] !== title) {
    t9 = <h3 className="text-[16px] leading-[22px] font-bold text-ds-primary">{title}</h3>;
    $[17] = title;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== cancel || $[20] !== items || $[21] !== shopId) {
    let t11;
    if ($[23] !== cancel || $[24] !== shopId) {
      t11 = order => <CustomOrderCard key={order.id} order={order} showShop={!shopId} busy={cancel.isPending && cancel.variables === order.id} onCancel={() => cancel.mutate(order.id)} />;
      $[23] = cancel;
      $[24] = shopId;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    t10 = items.map(t11);
    $[19] = cancel;
    $[20] = items;
    $[21] = shopId;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = <ul className="grid gap-3 md:grid-cols-2">{t10}</ul>;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== t9) {
    t12 = <section className="flex flex-col gap-4" data-testid="custom-order-list">{t9}{t11}</section>;
    $[28] = t11;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}
function _temp2(id) {
  return api.buyer.cancelCustomOrder(id);
}
function _temp(s) {
  return s.token;
}
function CustomOrderCard(t0) {
  const $ = _c(53);
  const {
    order,
    showShop,
    busy,
    onCancel
  } = t0;
  const {
    t
  } = useTranslation();
  const cancellable = order.status !== "accepted" && order.status !== "cancelled";
  const t1 = order.productType;
  const t2 = order.quantity;
  let t3;
  if ($[0] !== t) {
    t3 = t("shop.customOrder.pcs");
    $[0] = t;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== order.productType || $[3] !== order.quantity || $[4] !== t3) {
    t4 = <span className="truncate text-[14px] leading-[18px] font-semibold text-ds-primary">{t1} · {t2} {t3}</span>;
    $[2] = order.productType;
    $[3] = order.quantity;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== order.shopId || $[7] !== order.shopName || $[8] !== order.shopSlug || $[9] !== showShop) {
    t5 = showShop && order.shopName ? <><Link to={`/shop/${encodeURIComponent(order.shopSlug ?? order.shopId)}`} className="underline underline-offset-2 hover:text-ds-accent">{order.shopName}</Link>{" \xB7 "}</> : null;
    $[6] = order.shopId;
    $[7] = order.shopName;
    $[8] = order.shopSlug;
    $[9] = showShop;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== order.createdAt || $[12] !== t) {
    t6 = t("shop.customOrder.sentAt", {
      date: ruDate(order.createdAt)
    });
    $[11] = order.createdAt;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <span className="text-[12px] leading-4 text-ds-secondary">{t5}{t6}</span>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t4 || $[18] !== t7) {
    t8 = <div className="flex min-w-0 flex-col gap-0.5">{t4}{t7}</div>;
    $[17] = t4;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = STATUS_TONE[order.status];
  let t10;
  if ($[20] !== t9) {
    t10 = cn("shrink-0 rounded-full px-2.5 py-1 text-[12px] leading-4 font-semibold", t9);
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = `shop.customOrder.status.${order.status}`;
  let t12;
  if ($[22] !== t || $[23] !== t11) {
    t12 = t(t11);
    $[22] = t;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10 || $[26] !== t12) {
    t13 = <span className={t10} data-testid="custom-order-status">{t12}</span>;
    $[25] = t10;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13 || $[29] !== t8) {
    t14 = <div className="flex items-start justify-between gap-3">{t8}{t13}</div>;
    $[28] = t13;
    $[29] = t8;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== order.description) {
    t15 = <p className="line-clamp-2 text-[13px] leading-[18px] text-ds-primary">{order.description}</p>;
    $[31] = order.description;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== order.estimatePrice || $[34] !== t) {
    t16 = order.estimatePrice != null && <span className="text-[12px] leading-4 text-ds-secondary">{t("shop.customOrder.estimate", {
        price: rub(order.estimatePrice)
      })}</span>;
    $[33] = order.estimatePrice;
    $[34] = t;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== order.conversationId || $[37] !== order.status || $[38] !== t) {
    t17 = order.conversationId && <Button asChild={true} variant="gray" size="sm"><Link to={`/chats?conversation=${encodeURIComponent(order.conversationId)}`}>{order.status === "offer" ? t("shop.customOrder.viewOffer") : t("shop.customOrder.openChat")}</Link></Button>;
    $[36] = order.conversationId;
    $[37] = order.status;
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== busy || $[41] !== cancellable || $[42] !== onCancel || $[43] !== t) {
    t18 = cancellable && <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>{t("shop.customOrder.cancel")}</Button>;
    $[40] = busy;
    $[41] = cancellable;
    $[42] = onCancel;
    $[43] = t;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t17 || $[46] !== t18) {
    t19 = <div className="flex flex-wrap gap-2">{t17}{t18}</div>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t14 || $[49] !== t15 || $[50] !== t16 || $[51] !== t19) {
    t20 = <li className="flex flex-col gap-3 rounded-[16px] border border-ds-line p-4" data-testid="custom-order-card">{t14}{t15}{t16}{t19}</li>;
    $[48] = t14;
    $[49] = t15;
    $[50] = t16;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  return t20;
}